import { useState, memo, useMemo } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import classes from "./ConfirmSingleStatusChangeModal.module.scss";
import {
  ORDER_CANCELLED,
  ORDER_CONFIRMED,
  ORDER_PLACED,
  ORDER_RECEIVED,
} from "../../../constants/orderStatus";
import ColorButtons from "../ColorButtons";
import OrderDatesSummary from "../../order/OrderDatesSummary";
import StatusDateInput from "../../order/StatusDateInput";
import { getDefaultDateInput } from "../../../functions/order/orderDateUpdate";
import {
  buildStatusChangeNotices,
  VARIANT_SINGLE,
} from "../../../functions/order/orderDateMessages";
import { isRollback } from "../../../functions/order/statusTransition";

const style = {
  box: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
      xs: 320, // 0px以上の幅で360pxを適用
      sm: 360, // 600px以上の幅で400pxを適用
    },
    bgcolor: "background.paper",
    border: "none",
    borderRadius: "10px",
    boxShadow: 24,
    padding: "20px 0",
    maxHeight: "90vh",
    overflowY: "auto",
  },
};

const statusClass = (status) => {
  if (status === ORDER_PLACED) return classes.placed;
  if (status === ORDER_CONFIRMED) return classes.confirmed;
  if (status === ORDER_RECEIVED) return classes.received;
  return classes.canceled;
};

// 単票のステータス変更の確認ダイアログ。
// 管理者には「現在の4日付」と「今回記録する日付」を出し、工程を戻す操作では
// 既定で日付を維持する(元の注文日が黙って書き換わらないようにするため)。
export const ConfirmSingleStatuChangeModal = memo((props) => {
  const {
    targetStatus,
    setIsShow,
    onExec,
    onCancel,
    order,
    canEditDates = false,
  } = props;
  const [open, setOpen] = useState(true);
  const now = useMemo(() => new Date(), []);
  const [dateInput, setDateInput] = useState(() =>
    getDefaultDateInput([order], targetStatus, now)
  );

  const [isExecuting, setIsExecuting] = useState(false);

  const handleClose = () => {
    setIsShow(false);
    setOpen(false);
  };

  // 二度押しで同じ更新が2回走らないようにする
  const handleExec = () => {
    if (isExecuting) return;
    setIsExecuting(true);
    onExec(targetStatus, order, canEditDates ? dateInput : undefined);
  };

  const rollback = isRollback(order?.status, targetStatus);
  // 表示文言は functions/order/orderDateMessages.js に集約してある
  // (一括ダイアログと同じ関数を使い、案内が食い違わないようにする)
  const {
    dateField,
    dateLabel,
    isAutoRecorded,
    autoNotice,
    hint,
    remainingDatesNotice,
  } = buildStatusChangeNotices({
    orders: [order],
    targetStatus,
    dateInput,
    now,
    variant: VARIANT_SINGLE,
  });

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        // カレンダーのポップアップはPortalでbody直下(=このModalの外)に描画されるため、
        // フォーカストラップが有効だと日付を選んだ瞬間に焦点を奪い返されてしまう
        disableEnforceFocus
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style.box}>
          <div className={classes.root}>
            <div className={classes.container}>
              <div className={`${classes.title} ${classes.confirm}`}>
                <ErrorOutlineIcon className={classes.iconImage} />
                {`注文${order?.order_number ?? ""}を`}
                <p className={`${classes.status} ${statusClass(targetStatus)}`}>
                  {targetStatus}
                </p>
                にします
              </div>
              {rollback && (
                <div className={classes.rollback}>
                  {`⚠ ステータスを戻す操作です（${order?.status} → ${targetStatus}）`}
                </div>
              )}
              {canEditDates && remainingDatesNotice && (
                <div className={classes.staleNote}>{remainingDatesNotice}</div>
              )}
              {canEditDates && (
                <div className={classes.dateSection}>
                  <OrderDatesSummary
                    order={order}
                    layout="rows"
                    title="現在の日付"
                  />
                  {dateField && (
                    <StatusDateInput
                      label={dateLabel}
                      value={dateInput}
                      onChange={setDateInput}
                      hint={hint}
                      locked={isAutoRecorded}
                      notice={autoNotice}
                    />
                  )}
                </div>
              )}
              <div className={classes.spacer}></div>
              <div className={classes.message}>{"よろしいですか？"}</div>
              <div className={classes.buttons}>
                <ColorButtons
                  label={"はい"}
                  color="placed"
                  onClick={handleExec}
                  disabled={isExecuting}
                  width={90}
                  height={32}
                  bottom={0}
                />
                <ColorButtons
                  label={"いいえ"}
                  color="grey"
                  onClick={onCancel}
                  width={90}
                  height={32}
                  bottom={0}
                />
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
});
